/**
 * Status → Pill tone.
 *
 * The admin screens used to build a class name from the data
 * (`admin-badge-${status.toLowerCase()}`), which silently produced classes that
 * were never defined — `admin-badge-draft`, `admin-badge-article` and friends
 * had no rule, so those badges rendered as unstyled text. Mapping to a tone
 * makes the set of possible values explicit and type-checked.
 */

import type { PillTone } from '../../components/pill';

/**
 * Session lifecycle.
 *
 * These tones deliberately differ from the retired session-status stylesheet,
 * which painted SCHEDULED green, RUNNING red and CANCELLED amber. Red-for-live
 * read as an alarm on an ops screen; green now means live, blue means upcoming
 * and red means cancelled. Call sites pass `dot pulsing` for RUNNING to keep the
 * scan affordance the old stylesheet's animation provided.
 */
export function sessionStatusTone(status: string | undefined): PillTone {
  switch (status) {
    case 'RUNNING':
      return 'success';
    case 'FINISHED':
      return 'neutral';
    case 'CANCELLED':
      return 'danger';
    case 'DRAFT':
      return 'neutral';
    default:
      return 'info'; // SCHEDULED
  }
}

/** Account role. */
export function roleTone(role: string | undefined): PillTone {
  switch (role) {
    case 'PONDER_ADMIN':
      return 'brand';
    case 'MODERATOR':
      return 'info';
    default:
      // Not `success`: that is what activeTone(true) returns, so STUDENT and
      // ACTIVE rendered as the same green pill in adjacent columns and the role
      // column carried no signal of its own.
      return 'neutral';
  }
}

/**
 * Anything with an on/off state — worlds, cohorts, games, users, families.
 *
 * The inactive case is `neutral` (--text-soft), not `locked` (--text-dim):
 * --text-dim reads ~3.3:1 on a surface, and these are status labels, not
 * decoration. The rule this replaced had already been moved off --text-dim for
 * exactly that reason.
 */
export const activeTone = (isActive: boolean): PillTone => (isActive ? 'success' : 'neutral');

/**
 * Bulk-import operation: what the row will do when applied.
 *
 * `op.action` is an unconstrained `string` on the wire, and the markup used to
 * compose `bulk-import-badge--${action}`. An action the CSS did not anticipate
 * rendered as unstyled text — the same failure this file's header describes.
 */
export function importActionTone(action: string | undefined): PillTone {
  switch (action) {
    case 'create':
      return 'success';
    case 'update':
      return 'info';
    case 'delete':
      return 'danger';
    case 'replace':
      return 'warning';
    default:
      return 'neutral';
  }
}
